import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getEpisodes, getSeasons, JellyfinItem } from "../api/jellyfin";
import { Credentials } from "../storage/credentials";

type PlayerEpisodesInput = {
  credentials?: Credentials | null;
  item?: JellyfinItem;
  selectedSeasonId: string;
  setSelectedSeasonId: Dispatch<SetStateAction<string>>;
};

const sortEpisodes = (items?: JellyfinItem[]) => [...(items ?? [])].sort(
  (left, right) => (left.IndexNumber ?? 0) - (right.IndexNumber ?? 0),
);

export function usePlayerEpisodes({
  credentials,
  item,
  selectedSeasonId,
  setSelectedSeasonId,
}: PlayerEpisodesInput) {
  const seriesId = item?.SeriesId ?? "";
  const playbackSeasonId = item?.ParentId ?? "";
  const seasonQuery = useQuery({
    queryKey: ["player", "seasons", seriesId],
    queryFn: () => getSeasons(credentials!, seriesId),
    enabled: Boolean(credentials && seriesId),
  });
  const episodeQuery = useQuery({
    queryKey: ["player", "episodes", seriesId, selectedSeasonId],
    queryFn: () => getEpisodes(credentials!, seriesId, selectedSeasonId),
    enabled: Boolean(credentials && seriesId && selectedSeasonId),
  });
  const playbackEpisodesQuery = useQuery({
    queryKey: ["player", "episodes", seriesId, playbackSeasonId],
    queryFn: () => getEpisodes(credentials!, seriesId, playbackSeasonId),
    enabled: Boolean(credentials && seriesId && playbackSeasonId),
  });
  const seasons = useMemo(() => sortEpisodes(seasonQuery.data), [seasonQuery.data]);
  const episodes = useMemo(() => sortEpisodes(episodeQuery.data), [episodeQuery.data]);
  const playbackEpisodes = useMemo(
    () => sortEpisodes(playbackEpisodesQuery.data),
    [playbackEpisodesQuery.data],
  );
  const playbackEpisodeIndex = playbackEpisodes.findIndex((entry) => entry.Id === item?.Id);
  const playbackSeasonIndex = seasons.findIndex((entry) => entry.Id === playbackSeasonId);
  const previousSeasonId = playbackSeasonIndex > 0 ? seasons[playbackSeasonIndex - 1]?.Id ?? "" : "";
  const nextSeasonId = playbackSeasonIndex >= 0 && playbackSeasonIndex < seasons.length - 1
    ? seasons[playbackSeasonIndex + 1]?.Id ?? ""
    : "";
  const needsPreviousSeason = playbackEpisodeIndex === 0 && Boolean(previousSeasonId);
  const needsNextSeason = playbackEpisodeIndex >= 0 && playbackEpisodeIndex === playbackEpisodes.length - 1 && Boolean(nextSeasonId);
  const previousSeasonEpisodesQuery = useQuery({
    queryKey: ["player", "episodes", seriesId, previousSeasonId],
    queryFn: () => getEpisodes(credentials!, seriesId, previousSeasonId),
    enabled: Boolean(credentials && seriesId && needsPreviousSeason),
  });
  const nextSeasonEpisodesQuery = useQuery({
    queryKey: ["player", "episodes", seriesId, nextSeasonId],
    queryFn: () => getEpisodes(credentials!, seriesId, nextSeasonId),
    enabled: Boolean(credentials && seriesId && needsNextSeason),
  });
  const previousSeasonEpisodes = sortEpisodes(previousSeasonEpisodesQuery.data);
  const nextSeasonEpisodes = sortEpisodes(nextSeasonEpisodesQuery.data);

  useEffect(() => {
    if (!playbackSeasonId) return;
    setSelectedSeasonId(playbackSeasonId);
  }, [item?.Id, playbackSeasonId, setSelectedSeasonId]);

  const previousEpisode = playbackEpisodeIndex > 0
    ? playbackEpisodes[playbackEpisodeIndex - 1]
    : needsPreviousSeason
      ? previousSeasonEpisodes[previousSeasonEpisodes.length - 1]
      : undefined;
  const nextEpisode = playbackEpisodeIndex >= 0 && playbackEpisodeIndex < playbackEpisodes.length - 1
    ? playbackEpisodes[playbackEpisodeIndex + 1]
    : needsNextSeason
      ? nextSeasonEpisodes[0]
      : undefined;

  return {
    seasons,
    episodes,
    selectedSeason: seasons.find((entry) => entry.Id === selectedSeasonId),
    previousEpisode,
    nextEpisode,
    seasonQuery,
    episodeQuery,
  };
}
